import React from 'react';

// @ts-ignore - missing types for react-codesandboxer
import CodeSandboxer from 'react-codesandboxer';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { useFlags } from '@atlaskit/flag/use-flags';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

const iconSpacingStyles = cssMap({
	space050: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
});

const indexFile = `import React from 'react';
import ReactDOM from 'react-dom';
import '@atlaskit/css-reset';
import AppProvider from '@atlaskit/app-provider';

import Example from './example';

ReactDOM.render(
  <AppProvider>
    <Example />
  </AppProvider>,
  document.getElementById('root')
);
`;

export interface CodeSandboxProps {
	/**
	 * The git path to the example inside of the repo provided in `gitInfo`.
	 */
	examplePath: string;

	/**
	 * The source of the example.
	 *
	 * By providing this directly an extra network request can be avoided
	 * to improve loading times.
	 */
	example?: string;

	/**
	 * Used to name the sandbox.
	 */
	exampleName?: string;

	/**
	 * Used to resolve dependencies and make sure they're added to the sandbox.
	 */
	pkgJSON: {
		name: string;
		version: string;
		dependencies: object;
		devDependencies: object;
		peerDependencies: object;
	};

	/**
	 * Information for the git repository which contains this example.
	 * This repo will be queried to resolve relative imports.
	 *
	 * By default this will be the atlassian-frontend-mirror.
	 */
	gitInfo?: {
		account: string;
		repository: string;
		branch?: string;
		host: 'bitbucket' | 'github';
	};

	/**
	 * This is passed directly into the CodeSandboxer instance.
	 * It is used to replace relative imports with their corresponding package.
	 *
	 * See:
	 * <https://github.com/codesandbox/codesandboxer/tree/master/packages/react-codesandboxer#importreplacements-arraystring-string>.
	 */
	importReplacements?: Array<[string, string]>;
}

const afpGitInfo = {
	account: 'atlassian',
	repository: 'atlassian-frontend-mirror',
	branch: 'master',
	host: 'bitbucket',
};

/**
 * __Code sandbox__.
 *
 * A code sandbox is an icon that links to a codesandbox example.
 *
 */
const CodeSandbox = (props: CodeSandboxProps): React.JSX.Element => {
	const {
		examplePath,
		example,
		pkgJSON,
		gitInfo = afpGitInfo,
		exampleName,
		importReplacements,
	} = props;

	const { showFlag } = useFlags();

	// `workspace:^` versions do not work in Codesandbox, so we replace them with `latest`.
	const updatedPkgJSON = {
		...pkgJSON,
		dependencies: Object.fromEntries(
			Object.entries(pkgJSON.dependencies).map(([packageName, version]) => {
				return [packageName, version.replace(/^workspace:\^$/, 'latest')];
			}),
		),
		devDependencies: Object.fromEntries(
			Object.entries(pkgJSON.devDependencies).map(([packageName, version]) => {
				return [packageName, version.replace(/^workspace:\^$/, 'latest')];
			}),
		),
	};

	return (
		<CodeSandboxer
			gitInfo={gitInfo}
			examplePath={examplePath}
			example={example} // Can avoid an extra fetch by providing the source directly
			importReplacements={importReplacements}
			pkgJSON={updatedPkgJSON}
			dependencies={{
				[pkgJSON.name]: pkgJSON.version,
				'@emotion/react': '11.7.1', // Specifying the exact version to match platform package.json resolution
				'styled-components': '3.2.6', // Need to specify as it is a peer dependency (often of a transitive dependency)
				'@atlaskit/css-reset': 'latest',
				'@atlaskit/analytics-next': 'latest', // Dependency is injected at build time (prod bundle only)
				'@atlaskit/app-provider': 'latest',
			}}
			providedFiles={{
				'index.tsx': {
					content: indexFile, // Need to provide so we can add the css reset
				},
			}}
			name={exampleName}
			// Load the files when component mounts, instead of waiting for the button to be clicked.
			// This doesn't deploy the sandbox on mount yet, it only preloads all the data it needs to deploy.
			// This is not ideal, but necessary because of a bug with CodeSandboxer where it
			// opens the sandbox before the files are loaded and causes errors.
			preload
			skipRedirect
			afterDeploy={(_: any, sandboxId?: string) => {
				if (sandboxId) {
					// Unfortunately CodeSandboxer doesn't provide a way to use a standard link
					// because it needs to be deployed first, so we need to open a new window manually
					// once it's ready.
					window.open(
						// Append `?file=%2Fexample.tsx` to the URL to open the correct example file in the editor
						`https://codesandbox.io/p/sandbox/${sandboxId}?file=%2Fexample.tsx`,
						'_blank',
					);
				} else {
					// eslint-disable-next-line no-console
					console.error('CodeSandboxer failed to deploy the sandbox');

					if (showFlag) {
						showFlag({
							title: 'Error launching CodeSandbox',
							description: 'There was an issue launching the example in CodeSandbox.',
							icon: (
								<Flex xcss={iconSpacingStyles.space050}>
									<StatusErrorIcon label="" color={token('color.icon.inverse')} />
								</Flex>
							),
							isAutoDismiss: true,
							appearance: 'error',
						});
					}
				}
			}}
		>
			{({ isLoading, isDeploying }: { isLoading: boolean; isDeploying: boolean }) => {
				const isCodeSandboxLoading = isLoading || isDeploying;

				return (
					<Button
						appearance="subtle"
						iconAfter={LinkExternalIcon}
						isLoading={isCodeSandboxLoading}
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'openCodesandboxExample', {
								packageName: pkgJSON.name,
								exampleName,
							});
						}}
						type="submit"
					>
						Edit in code sandbox<VisuallyHidden> (opens in a new window)</VisuallyHidden>
					</Button>
				);
			}}
		</CodeSandboxer>
	);
};

export default CodeSandbox;
